import { useEffect, useMemo, useRef, useState } from 'react'

import {
  calcDefaultValues,
  calcExportData,
  calcModeParamsSection,
  calcReportParamsSection,
  resolveModeState,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/bpm/lib/report-params'

// Whatever the active mode already holds wins; the rest of its values come from its defaults, so a
// sub-mode is never entered half-resolved.
const withModeState = (state, context) => ({ ...resolveModeState({ mode: state.mode, ...context }), ...state })

// Entering a sub-mode starts it over: its defaults first, then whatever the entry itself spells out.
// The packed value only ever carries the active mode, so a sub-mode left behind holds nothing worth
// restoring - and the screen agrees with what a reload would give.
const enterMode = ({ base, values, context }) => ({
  ...base,
  ...resolveModeState({ mode: values.mode || base.mode, ...context }),
  ...values
})

// Owns the brand-perceptual-mapping block state: the active mode and that mode's values, the
// sort order among them. It is all packed into bpm<key>, so the block is seeded from that one
// section and re-seeded whenever it changes outside the block.
//
// The page view never parses those sections: it receives an api on mount and pulls the payloads
// it needs (the section to write, the export payload) when a change is announced through
// onStateChange. A mode switch is announced as its own reason, since the view rebuilds for it.
export default function useBpmState({
  params,
  paramsRevision,
  modes,
  defaultMode,
  defaultPlotByValue,
  questionVariants,
  batches,
  selectOptions,
  selectOptionsWithNone,
  onReady,
  onStateChange
}) {
  const defaultValues = useMemo(
    () => calcDefaultValues({ questionVariants, batches, defaultPlotByValue }),
    [questionVariants, batches, defaultPlotByValue]
  )

  const context = {
    modes,
    defaultMode,
    defaultValues,
    questionVariants,
    batches,
    selectOptions,
    selectOptionsWithNone
  }

  const needsParamsUpdate = useRef(false)
  const modeOnlyUpdate = useRef(false)

  const [state, setState] = useState(() => {
    const resolved = resolveReportParams({ section: params?.bpm, ...context })
    needsParamsUpdate.current = resolved.needsUpdate
    modeOnlyUpdate.current = resolved.modeOnly

    return withModeState(resolved.state, context)
  })

  const snapshot = useRef()
  snapshot.current = { state, context }

  const pendingReason = useRef('seed')

  const onModelChange = useMemo(
    () =>
      (change = {}) =>
        setState(current => {
          if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

          const switched = undefined !== change.mode && change.mode !== current.mode
          pendingReason.current = switched ? 'mode' : 'state'

          const context = snapshot.current.context

          return switched
            ? enterMode({ base: { ...current, ...change }, values: change, context })
            : withModeState({ ...current, ...change }, context)
        }),
    []
  )

  const api = useMemo(
    () => ({
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current

        return modeOnlyUpdate.current
          ? calcModeParamsSection({ mode: state.mode, defaultMode: context.defaultMode })
          : calcReportParamsSection({ state, ...context })
      },
      getExportData: () => calcExportData({ state: snapshot.current.state, ...snapshot.current.context }),
      update: onModelChange
    }),
    [onModelChange]
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // The section moved outside the block (a report revert, another view of the question, the URL):
  // re-seed from it.
  //
  // Keyed on the revision the shell bumps per host render, not on the section inside the params
  // bag. A host-driven render is a fresh read of the live model; a re-render React starts for
  // itself carries the revision it already had. Keying on the section missed a restore of a mode
  // the block had already moved away from locally, because the block's own write is silent -- the
  // section the block was handed had never changed, so there was nothing for a comparison to
  // notice, and a revert landed back on the very value props last carried.
  useEffect(() => {
    const resolved = resolveReportParams({ section: params?.bpm, ...snapshot.current.context })

    needsParamsUpdate.current = needsParamsUpdate.current || resolved.needsUpdate
    modeOnlyUpdate.current = resolved.modeOnly
    pendingReason.current = 'seed'

    setState(current =>
      enterMode({
        base: current,
        values: resolved.state,
        context: snapshot.current.context
      })
    )
  }, [params, paramsRevision])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    const needsUpdate = needsParamsUpdate.current
    needsParamsUpdate.current = false

    if (reason) onStateChangeRef.current?.(reason, { needsUpdate })
  }, [state])

  return { ...state, defaultValues, onModelChange }
}
